import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../../../context/ThemeContext/ThemeContext';
import { useAuth } from '../../../../../context/AuthContext/AuthContext';
import { isImpersonating } from '../../../../../utils/impersonationSession';
import LanguageMenu from '../LanguageMenu/LanguageMenu';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  AssignmentInd as AssignmentIndIcon,
  CancelOutlined as CancelOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  GppMaybe as GppMaybeIcon,
  Logout as LogoutIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material';

const SIDEBAR_TRANSITION_DURATION_MS = 420;

const ProfileMenu = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isDarkMode: _isDarkMode, toggleTheme: _toggleTheme } = useThemeContext();
  const { logout, profile } = useAuth();
  const isAdminView = isImpersonating();
  const { i18n, t } = useTranslation(['header']);
  const navigate = useNavigate();
  const isRtl = i18n.language === 'ar';

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isSecondMenuOpen, setIsSecondMenuOpen] = useState(false);
  const [navigationPath, setNavigationPath] = useState<string | null>(null);
  const [anchorIsRtl, setAnchorIsRtl] = useState(isRtl);

  // isRtl changed but anchorIsRtl hasn't caught up yet → hide immediately (same render)
  const menuHidden = isRtl !== anchorIsRtl;

  // Reposition during sidebar transition, then update anchor side and show
  useEffect(() => {
    if (isRtl !== anchorIsRtl) {
      const start = Date.now();
      const duration = SIDEBAR_TRANSITION_DURATION_MS;
      let raf: number;
      const tick = () => {
        window.dispatchEvent(new Event('resize'));
        if (Date.now() - start < duration) {
          raf = requestAnimationFrame(tick);
        } else {
          setAnchorIsRtl(isRtl);
        }
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }
  }, [isRtl, anchorIsRtl]);

  const languageNames = {
    en: 'English',
    fr: 'Français',
    es: 'Español',
    ar: 'العربية',
  };

  const languageFullName =
    languageNames[i18n.language as keyof typeof languageNames] || '...';

  const openMenu = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setIsSecondMenuOpen(false);
  };

  const handleOpenSecondMenu = () => {
    setIsSecondMenuOpen(true);
  };

  const handleCloseSecondMenu = () => {
    setIsSecondMenuOpen(false);
  };

  const handleMenuItemClick = (path: string) => {
    setNavigationPath(path);
    handleMenuClose();
  };

  if (!profile) {
    return <CircularProgress size={24} />;
  }

  // --- Helpers for KYC Status UI ---

  /**
   * Returns the translated label for the KYC status.
   */
  const getKycStatusLabel = (status: string): string => {
    switch (status) {
      case 'APPROVED':
        return t('MENU_PROFILE.KYC.STATUS.COMPLETED');
      case 'SUSPECTED':
      case 'DENIED':
        return t('MENU_PROFILE.KYC.STATUS.REJECTED');
      case 'PENDING':
      case 'AWAITING_SELF_REVIEW':
      case 'AWAITING_KYC_PROCESS':
      case 'APPROVED_WITH_MISMATCH':
        return t('MENU_PROFILE.KYC.STATUS.IN_PROGRESS');
      default:
        return t('MENU_PROFILE.KYC.STATUS.NOT_STARTED');
    }
  };

  /**
   * Returns the icon and style for the KYC Chip based on status.
   */
  const getKycChipData = (status: string) => {
    const iconStyle = (color: string) => ({
      color: `${color} !important`,
      fontSize: '1.2rem',
    });

    const wrapIcon = (icon: React.ReactElement, color: string) => (
      <Box
        component="span"
        sx={{
          color,
          display: 'flex',
          alignItems: 'center',
          borderRadius: '25px',
        }}
      >
        {icon}
      </Box>
    );

    switch (status) {
      case 'APPROVED':
        return {
          icon: wrapIcon(
            <CheckCircleOutlineIcon sx={iconStyle('#4caf50')} />,
            '#4caf50',
          ),
          sx: {
            backgroundColor: 'rgba(76, 175, 80, 0.15)',
            color: '#4caf50',
            border: '1px solid rgba(76, 175, 80, 0.3)',
          },
        };
      case 'PENDING':
        return {
          icon: wrapIcon(
            <AccessTimeIcon sx={iconStyle('#FBBF24')} />,
            '#FBBF24',
          ),
          sx: {
            backgroundColor: 'rgba(255, 152, 0, 0.15)',
            color: '#FBBF24',
            border: '1px solid rgba(255, 152, 0, 0.3)',
          },
        };
      case 'DENIED':
      case 'SUSPECTED':
        return {
          icon: wrapIcon(
            <CancelOutlinedIcon sx={iconStyle('#f44336')} />,
            '#f44336',
          ),
          sx: {
            backgroundColor: 'rgba(244, 67, 54, 0.15)',
            color: '#f44336',
            border: '1px solid rgba(244, 67, 54, 0.3)',
          },
        };
      default:
        return {
          icon: wrapIcon(<GppMaybeIcon sx={iconStyle('#9e9e9e')} />, '#9e9e9e'),
          sx: {
            backgroundColor: 'rgba(158, 158, 158, 0.15)',
            color: '#9e9e9e',
            border: '1px solid rgba(158, 158, 158, 0.3)',
          },
        };
    }
  };

  const kycStatus = profile?.kycStatus || 'NOT_STARTED';
  const chipData = getKycChipData(kycStatus);

  return (
    <>
      <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
        <Avatar sx={{ bgcolor: '#354EEF', color: '#FFFFFF', '& svg': { width: '55%', height: '55%' } }} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={openMenu && !isSecondMenuOpen}
        onClose={handleMenuClose}
        TransitionProps={{
          onExited: () => {
            if (navigationPath) {
              navigate(navigationPath);
              setNavigationPath(null);
            }
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: anchorIsRtl ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: anchorIsRtl ? 'left' : 'right',
        }}
        sx={{
          mt: 2,
          '& .MuiPaper-root': {
            ...(menuHidden && {
              opacity: '0 !important',
              pointerEvents: 'none',
              transition: 'none !important',
            }),
          },
        }}
      >
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
          sx={{
            borderBottom: '1px solid #949494',
            padding: '8px 16px',
            marginBottom: '16px',
          }}
        >
          <Tooltip
            title={profile?.email ?? ''}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -14],
                    },
                  },
                ],
              },
            }}
          >
            <Typography variant="subtitle1" component={'p'}>
              {`${profile?.firstname ?? ''} ${profile?.lastname ?? ''}`}
            </Typography>
          </Tooltip>

          <IconButton onClick={handleMenuClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <MenuItem onClick={() => handleMenuItemClick('/profile')}>
          <ListItemIcon>
            <AssignmentIndIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="inherit">
                {t('MENU_PROFILE.PROFILE')}
              </Typography>
              <Tooltip title={getKycStatusLabel(kycStatus)} placement="top">
                <Chip
                  label={t('MENU_PROFILE.KYC_LABEL')}
                  icon={chipData.icon}
                  size="small"
                  sx={{
                    height: 24,
                    borderRadius: 20,
                    fontWeight: 500,
                    fontSize: '0.7rem',
                    px: 0.5,
                    ml: 2,
                    cursor: 'pointer',
                    ...chipData.sx,
                  }}
                />
              </Tooltip>
            </Stack>
          </ListItemText>
        </MenuItem>

        <MenuItem onClick={handleOpenSecondMenu}>
          <ListItemIcon>
            <TranslateIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {t('MENU_PROFILE.LANGUAGE')} {languageFullName}
          </ListItemText>
          <ChevronRightIcon />
        </MenuItem>

        <Tooltip
          title={isAdminView ? t('MENU_PROFILE.CANNOT_LOGOUT_ADMIN_VIEW') : ''}
          placement="left"
        >
          {/* Span wrapper required for Tooltip to work with disabled MenuItem */}
          <span>
            <MenuItem
              disabled={isAdminView}
              onClick={() => {
                handleMenuClose();
                logout();
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('MENU_PROFILE.LOG_OUT')}</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
      <LanguageMenu
        anchorEl={anchorEl}
        open={isSecondMenuOpen}
        handleBack={handleCloseSecondMenu}
        handleClose={handleMenuClose}
        menuHidden={menuHidden}
        anchorIsRtl={anchorIsRtl}
      />
    </>
  );
};

export default ProfileMenu;